import { useCallback, useEffect, useRef, useState, type CSSProperties } from "react";
import { cn } from "@/lib/cn";
import { blurUrl, imageUrl, isSafeImageUrl, srcSet } from "@/lib/image";

export interface SmartImageProps {
  /** Image URL. Cloudinary URLs get responsive srcset, format/quality transforms and a blurred preview. */
  src: string | null | undefined;
  /**
   * Required (TypeScript enforces it). Describe the image, or pass "" when it is decorative
   * because nearby text already says what it shows.
   */
  alt: string;
  /** Art-directed image for screens up to 640px wide (e.g. a square hero on phones). */
  mobileSrc?: string | null;
  /** CSS aspect-ratio of the box, e.g. "1 / 1" or "16 / 6". Keeps layout stable before the image loads. */
  aspectRatio?: string;
  /** Default rendered width in CSS pixels, used for the src transform. */
  width?: number;
  /** Largest srcset candidate. */
  maxWidth?: number;
  sizes?: string;
  /** Above-the-fold image: eager load, high fetch priority. */
  priority?: boolean;
  fit?: "cover" | "contain";
  /** Classes for the outer box (size, rounding, position). */
  className?: string;
  /** Classes for the <img> itself (e.g. hover zoom). */
  imgClassName?: string;
  /** Shown in the fallback when the image fails and `alt` is empty. */
  fallbackLabel?: string;
  /** Smaller fallback (icon only) for thumbnails and avatars. */
  compact?: boolean;
  style?: CSSProperties;
  /** Inline style for the <img> (e.g. a zoom transform). */
  imgStyle?: CSSProperties;
  onLoad?: () => void;
  onError?: () => void;
}

type Status = "loading" | "loaded" | "error";

/**
 * Every content image goes through this: a fixed-ratio box that never collapses, a blurred preview,
 * a fade-in on load, and a branded fallback instead of a blank box when the image can't load.
 *
 * <SmartImage src={banner.image} mobileSrc={banner.mobile_image} alt="" aspectRatio="1920 / 720" priority />
 */
export function SmartImage({
  src,
  alt,
  mobileSrc,
  aspectRatio,
  width = 640,
  maxWidth = 1440,
  sizes = "100vw",
  priority = false,
  fit = "cover",
  className,
  imgClassName,
  fallbackLabel,
  compact = false,
  style,
  imgStyle,
  onLoad,
  onError,
}: SmartImageProps) {
  const usable = isSafeImageUrl(src);
  const [status, setStatus] = useState<Status>(usable ? "loading" : "error");
  const ref = useRef<HTMLImageElement>(null);

  // A new src starts over.
  useEffect(() => {
    setStatus(isSafeImageUrl(src) ? "loading" : "error");
  }, [src, mobileSrc]);

  // Cached images can finish before React attaches onLoad.
  useEffect(() => {
    const img = ref.current;
    if (img?.complete) setStatus(img.naturalWidth > 0 ? "loaded" : "error");
  }, [src, mobileSrc]);

  const loaded = useCallback(() => {
    setStatus("loaded");
    onLoad?.();
  }, [onLoad]);
  const failed = useCallback(() => {
    setStatus("error");
    onError?.();
  }, [onError]);

  const preview = usable ? blurUrl(src) : "";
  const boxStyle: CSSProperties = {
    ...(aspectRatio ? { aspectRatio } : null),
    ...(preview && status === "loading"
      ? { backgroundImage: `url("${preview}")`, backgroundSize: "cover", backgroundPosition: "center" }
      : null),
    ...style,
  };

  return (
    <span className={cn("relative block overflow-hidden bg-elevated", className)} style={boxStyle} data-image-status={status}>
      {status === "loading" && !preview && <span aria-hidden className="absolute inset-0 animate-pulse bg-fg/[0.03]" />}
      {status === "error" ? (
        <ImageFallback label={alt || fallbackLabel || ""} compact={compact} />
      ) : (
        <picture>
          {mobileSrc && isSafeImageUrl(mobileSrc) && (
            <source media="(max-width: 640px)" srcSet={srcSet(mobileSrc, 800) ?? imageUrl(mobileSrc, 800)} sizes="100vw" />
          )}
          <img
            ref={ref}
            src={imageUrl(src, width)}
            srcSet={srcSet(src, maxWidth)}
            sizes={srcSet(src, maxWidth) ? sizes : undefined}
            alt={alt}
            loading={priority ? "eager" : "lazy"}
            decoding="async"
            // React 18 only forwards the lowercase attribute.
            {...{ fetchpriority: priority ? "high" : "auto" }}
            style={imgStyle}
            draggable={false}
            onLoad={loaded}
            onError={failed}
            className={cn(
              "size-full transition-[opacity,transform] duration-500 motion-reduce:transition-none",
              fit === "cover" ? "object-cover" : "object-contain",
              status === "loaded" ? "opacity-100" : "opacity-0",
              imgClassName,
            )}
          />
        </picture>
      )}
    </span>
  );
}

/** Dark branded placeholder: the Orb outline and the image's description. Never a blank box. */
export function ImageFallback({ label, compact = false }: { label?: string; compact?: boolean }) {
  return (
    <span
      role={label ? "img" : undefined}
      aria-label={label || undefined}
      aria-hidden={label ? undefined : true}
      className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-[radial-gradient(circle_at_50%_40%,rgb(var(--rgb-primary)/0.18),transparent_65%)] p-3 text-center"
    >
      <svg viewBox="0 0 64 64" className={cn("text-primary-text/70 shrink-0", compact ? "size-7" : "size-12")} aria-hidden>
        <circle cx="32" cy="32" r="17" fill="none" stroke="currentColor" strokeWidth="2.5" />
        <ellipse cx="32" cy="34" rx="29" ry="8.5" fill="none" stroke="currentColor" strokeOpacity=".55" strokeWidth="2" />
        <path d="M27 31v-4a5 5 0 0 1 10 0v4" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
        <rect x="25" y="31" width="14" height="10" rx="2.5" fill="currentColor" fillOpacity=".35" stroke="currentColor" strokeWidth="2" />
      </svg>
      {!compact && label && <span className="line-clamp-2 max-w-[16rem] text-xs text-ink-muted">{label}</span>}
    </span>
  );
}
